import { _ } from '@ngx-translate/core';
import { featureFlag, type FeatureFlag } from '@sb/shared/feature';

import { type LabsFeatureWithIcon } from './labs-dialog.model';

/** Shared with the payroll prep flow, which offers the same survey from its own page. */
export const PAYROLL_PREP_SURVEY_ID = 'labs-payroll-prep-feedback';

/** Shared with the timesheet priority view, which offers the same survey from its toolbar. */
export const TIMESHEET_PRIORITY_VIEW_SURVEY_ID = 'labs-timesheet-priority-view-feedback';

export const labsItemList: LabsFeatureWithIcon[] = [
  {
    featureFlag: featureFlag.LABS_COMMAND_PALETTE,
    modalFeatureFlags: [featureFlag.LABS_MODAL_COMMAND_PALETTE],
    icon: 'hotkey-regular',
    title: _('Command Palette'),
    description: _(
      'Jump to any page or action by typing. Press Cmd+K (Mac) or Ctrl+K (Windows/Linux) and search for what you need, for example "dashboard" or "search employee". Available on web only.',
    ),
    surveyId: 'web-labs-command-palette',
  },
  {
    featureFlag: featureFlag.LABS_TIMESHEET_LIST_VIEW,
    // Availability is driven by the two *modal* flags (web + mobile), not the
    // opt-in labs_(mobile_)timesheet_list_view flags — those are gated on the
    // isLabsUser context and would make the copy vary per user.
    modalFeatureFlags: [featureFlag.LABS_MODAL_TIMESHEET_LIST_VIEW, featureFlag.LABS_MOBILE_MODAL_TIMESHEET_LIST_VIEW],
    icon: 'report-forms-regular',
    title: _('Timesheet priority view'),
    // Web-only copy: the default, and the fallback when only the web modal flag
    // is on. The web+mobile and mobile-only variants live in descriptionByFlag.
    description: _(
      'Review and approve timesheets faster with a new priority view that groups entries by priority and highlights the ones that need your attention. Available on web only.',
    ),
    descriptionByFlag: {
      all: _(
        'Review and approve timesheets faster with a new priority view that groups entries by priority and highlights the ones that need your attention. Available on web and mobile. On mobile, the Priority tab replaces the Clocked in tab.',
      ),
      [featureFlag.LABS_MOBILE_MODAL_TIMESHEET_LIST_VIEW]: _(
        'Review and approve timesheets faster with a new Priority tab that groups entries by priority and highlights the ones that need your attention. Available on mobile only. The Priority tab replaces the Clocked in tab.',
      ),
    },
    surveyId: TIMESHEET_PRIORITY_VIEW_SURVEY_ID,
  },
  {
    featureFlag: featureFlag.LABS_PAYROLL_PREP,
    // Availability is driven by the *modal* flag, not the opt-in flag, which is gated on
    // the isLabsUser context and would make this copy vary per user.
    modalFeatureFlags: [featureFlag.LABS_MODAL_PAYROLL_PREP],
    icon: 'wallet-4-regular',
    title: _('Prepare payroll'),
    description: _(
      'Work through payroll preparation in one guided flow: approve absences and timesheets, correct plus/min hours, lock the period and preview your payroll data. Available on web only.',
    ),
    surveyId: PAYROLL_PREP_SURVEY_ID,
  },
];

/**
 * Resolves a card's description for the current rollout. Returns the `all` copy
 * when every modal flag is enabled, otherwise the copy for a single enabled
 * flag, falling back to the card's plain `description`.
 */
export const resolveLabsItemDescription = (
  item: LabsFeatureWithIcon,
  isEnabled: (flag: FeatureFlag) => boolean,
): string => {
  const { descriptionByFlag, description, modalFeatureFlags } = item;

  if (!descriptionByFlag) {
    return description;
  }

  const enabledFlags = modalFeatureFlags.filter(isEnabled);

  if (enabledFlags.length === modalFeatureFlags.length && descriptionByFlag.all) {
    return descriptionByFlag.all;
  }

  // Exactly one modal flag enabled → its per-flag copy. Any other partial
  // rollout (2+ but not all) falls back to the plain description, so multi-flag
  // cards can't silently pick the "first" enabled flag's copy.
  if (enabledFlags.length === 1) {
    return descriptionByFlag[enabledFlags[0]] ?? description;
  }

  return description;
};
